import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
  Avatar, Button, CssBaseline, FormControl, FormControlLabel, Checkbox,
  Input, InputLabel, Paper, Typography
} from '@material-ui/core';
import LockIcon from '@material-ui/icons/LockOutlined';
import withStyles from '@material-ui/core/styles/withStyles';
import styles from './styles';

import { login } from 'redux/actions/user';
import Rest from 'tools/rest';

/** Совпадает с PhoneConfirmation::EXPIRE_TIME_CRM (40.seconds) после успешной отправки */
const PHONE_CODE_RESEND_COOLDOWN_SEC = 40;

function parseThrottleSecondsFromMessage(text) {
  if (!text || typeof text !== 'string') return null;
  const m = text.match(/через (\d+) секунд/);
  return m ? parseInt(m[1], 10) : null;
}

function pickFirstPhoneRequestError(errors) {
  if (!errors || typeof errors !== 'object') return null;
  for (const key of ['login', 'phone', 'allow_two_factor_auth', 'code', 'base']) {
    if (Array.isArray(errors[key]) && errors[key][0]) return errors[key][0];
  }
  if (Array.isArray(errors.full_messages) && errors.full_messages[0]) {
    return errors.full_messages[0];
  }
  return null;
}

class Login extends Component {
  state = {
    redirectToReferrer: false,
    authMode: 'password',
    codeSent: false,
    loading: false,
    errorText: null,
    login: '',
    resendSecondsLeft: 0,
  };

  _resendIntervalId = null;

  componentWillUnmount() {
    this.clearResendCooldown();
  }

  clearResendCooldown = () => {
    if (this._resendIntervalId) {
      clearInterval(this._resendIntervalId);
      this._resendIntervalId = null;
    }
  };

  startResendCooldown = (seconds) => {
    this.clearResendCooldown();
    const s = Math.max(0, Math.ceil(Number(seconds) || 0));
    this.setState({ resendSecondsLeft: s });
    if (s <= 0) return;
    this._resendIntervalId = setInterval(() => {
      this.setState((prev) => {
        const next = prev.resendSecondsLeft - 1;
        if (next <= 0) {
          this.clearResendCooldown();
          return { resendSecondsLeft: 0 };
        }
        return { resendSecondsLeft: next };
      });
    }, 1000);
  };

  handleChange = (e) => {
    const { name, value } = e.target;

    this.setState({
      [name]: value,
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { email, password, remember_me } = this.state;

    this.setState({ loading: true, errorText: null });
    Rest.post('/users/sign_in.json', { user: { email, password, remember_me } })
      .then(json => {
        this.setState({ redirectToReferrer: true });
        this.props.Login(json.data);
      })
      .catch(e => {
        const errorText = e?.response?.data?.error || 'Ошибка авторизации';
        this.setState({ errorText });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  doRequestPhoneCode = () => {
    const { login } = this.state;

    if (!login || !login.trim()) {
      this.setState({ errorText: 'Введите логин пользователя' });
      return;
    }

    this.setState({ loading: true, errorText: null });
    Rest.post('/users/sign_in/phone.json', { user: { login: login.trim() } })
      .then(() => {
        this.setState({ codeSent: true });
        this.startResendCooldown(PHONE_CODE_RESEND_COOLDOWN_SEC);
      })
      .catch((e) => {
        const errors = e?.response?.data?.errors || {};
        const errorText = pickFirstPhoneRequestError(errors) || 'Ошибка отправки кода';
        const throttleSec = parseThrottleSecondsFromMessage(errorText);
        if (throttleSec != null) {
          this.startResendCooldown(throttleSec);
        }
        this.setState({ errorText });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  requestPhoneCode = (e) => {
    e.preventDefault();
    this.doRequestPhoneCode();
  };

  submitPhoneCode = (e) => {
    e.preventDefault();
    const { login, code } = this.state;

    if (!login || !login.trim()) {
      this.setState({ errorText: 'Введите логин пользователя' });
      return;
    }

    this.setState({ loading: true, errorText: null });
    Rest.post('/users/sign_in/confirm_phone.json', { user: { login: login.trim(), code } })
      .then((json) => {
        this.setState({ redirectToReferrer: true });
        this.props.Login(json.data);
      })
      .catch((e) => {
        const errors = e?.response?.data?.errors || {};
        const errorText = errors?.code?.[0] || errors?.login?.[0] || errors?.phone?.[0] || errors?.allow_two_factor_auth?.[0] || 'Ошибка подтверждения кода';
        this.setState({ errorText });
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  render() {
    const { classes, user } = this.props;
    const { from } = this.props.location.state || { from: { pathname: '/' } };
    const { authMode, codeSent, loading, errorText, resendSecondsLeft } = this.state;

    if (this.state.redirectToReferrer || user) return <Redirect to={from} />;

    return (
      <React.Fragment>
        <CssBaseline />
        <main className={classes.layout}>
          <Paper className={classes.paper}>
            <Avatar className={classes.avatar}>
              <LockIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              Sign in
            </Typography>
            <div style={{ display: 'flex', width: '100%', marginTop: 12, gap: 8 }}>
              <Button
                fullWidth
                variant={authMode === 'password' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => {
                  this.clearResendCooldown();
                  this.setState({
                    authMode: 'password',
                    errorText: null,
                    login: '',
                    codeSent: false,
                    resendSecondsLeft: 0,
                  });
                }}
              >
                Email
              </Button>
              <Button
                fullWidth
                variant={authMode === 'phone' ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => {
                  this.clearResendCooldown();
                  this.setState({
                    authMode: 'phone',
                    errorText: null,
                    codeSent: false,
                    resendSecondsLeft: 0,
                  });
                }}
              >
                2FA
              </Button>
            </div>

            {errorText && (
              <Typography style={{ color: '#f44336', marginTop: 10, textAlign: 'center' }}>
                {errorText}
              </Typography>
            )}

            {authMode === 'password' ? (
              <form className={classes.form} onSubmit={this.handleSubmit}>
                <FormControl margin="normal" required fullWidth>
                  <InputLabel htmlFor="email">Email Address</InputLabel>
                  <Input id="email" name="email" autoComplete="email" autoFocus onChange={this.handleChange} />
                </FormControl>
                <FormControl margin="normal" required fullWidth>
                  <InputLabel htmlFor="password">Password</InputLabel>
                  <Input
                    name="password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    onChange={this.handleChange}
                  />
                </FormControl>
                <FormControlLabel
                  control={<Checkbox value="remember" color="primary" onChange={(e) => {
                    this.setState({
                      remember_me: e.target.checked ? true : undefined,
                    });
                  }} />}
                  label="Remember me"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={loading}
                >
                  Sign in
                </Button>
              </form>
            ) : (
              <form className={classes.form} onSubmit={codeSent ? this.submitPhoneCode : this.requestPhoneCode}>
                <FormControl margin="normal" required fullWidth>
                  <InputLabel htmlFor="login">Логин</InputLabel>
                  <Input
                    id="login"
                    name="login"
                    value={this.state.login}
                    placeholder="Логин пользователя"
                    autoComplete="username"
                    autoFocus
                    onChange={this.handleChange}
                  />
                </FormControl>
                {codeSent && (
                  <>
                    <FormControl margin="normal" required fullWidth>
                      <InputLabel htmlFor="code">Код из SMS</InputLabel>
                      <Input
                        id="code"
                        name="code"
                        inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
                        onChange={this.handleChange}
                      />
                    </FormControl>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, alignItems: 'center' }}>
                      <Button
                        type="button"
                        size="small"
                        onClick={() => {
                          this.clearResendCooldown();
                          this.setState({
                            codeSent: false,
                            code: '',
                            errorText: null,
                            resendSecondsLeft: 0,
                          });
                        }}
                      >
                        Изменить логин
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        color="primary"
                        disabled={loading || resendSecondsLeft > 0}
                        onClick={() => this.doRequestPhoneCode()}
                      >
                        {resendSecondsLeft > 0
                          ? `Повторная отправка через ${resendSecondsLeft} с`
                          : 'Отправить код повторно'}
                      </Button>
                    </div>
                  </>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={loading}
                >
                  {codeSent ? 'Войти по коду' : 'Получить код'}
                </Button>
              </form>
            )}
          </Paper>
        </main>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state =>
({
  user: state.user,
});

const mapDispatchToProps = dispatch =>
({
  Login(payload) {
    dispatch(login(payload));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Login));

